import React from 'react';
import { useFormatCurrency } from '../../hooks/useFormatters';

export type DirectDebitRunSummaryData = {
    count: number;
    totalAmount: number;
    categoryBreakdown: Record<string, number>;
};

/** Colours for category pills (cycle if more categories than colours) */
const CATEGORY_COLOURS = [
    { bg: '#e6f4ea', text: '#0d6832', border: '#a8d5ba' },
    { bg: '#e8f0fe', text: '#1a56db', border: '#aecbfa' },
    { bg: '#fef3e2', text: '#b45309', border: '#fcd9b8' },
    { bg: '#f3e8fd', text: '#6b21a8', border: '#d8b4fe' },
    { bg: '#fce8e6', text: '#b91c1c', border: '#fecaca' },
];

export type DirectDebitRunSummaryProps = {
    summary: DirectDebitRunSummaryData;
    /** Label for the count line, e.g. "Selected direct debits" or "Direct debits in run" */
    countLabel?: string;
    /** Label for the total line, e.g. "Total amount this run" or "Total collected" */
    totalLabel?: string;
    /** Label for the category breakdown section */
    categoryLabel?: string;
    /** Optional inline styles for the root container */
    style?: React.CSSProperties;
};

const DirectDebitRunSummary: React.FC<DirectDebitRunSummaryProps> = ({
    summary,
    countLabel = 'Direct debits',
    totalLabel = 'Total amount',
    categoryLabel = 'By category',
    style,
}) => {
    const format = useFormatCurrency();
    const entries = Object.entries(summary.categoryBreakdown);
    const hasBreakdown = entries.length > 0;

    return (
        <div
            style={{
                marginBottom: '16px',
                padding: '16px 20px',
                backgroundColor: '#fafbfc',
                border: '1px solid #e8eaed',
                borderRadius: 8,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                ...style,
            }}
        >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '20px 24px', marginBottom: hasBreakdown ? 14 : 0 }}>
                <div>
                    <span style={{ fontSize: 12, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                        {countLabel}
                    </span>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#202124', marginTop: 2 }}>
                        {summary.count}
                    </div>
                </div>
                <div>
                    <span style={{ fontSize: 12, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                        {totalLabel}
                    </span>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0d6832', marginTop: 2 }}>
                        {format(summary.totalAmount)}
                    </div>
                </div>
            </div>
            {hasBreakdown && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e8eaed' }}>
                    <div style={{ fontSize: 11, color: '#80868b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>
                        {categoryLabel}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {entries.map(([category, amount], i) => {
                            const { bg, text, border } = CATEGORY_COLOURS[i % CATEGORY_COLOURS.length];
                            return (
                                <span
                                    key={category}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '6px 10px',
                                        borderRadius: 6,
                                        backgroundColor: bg,
                                        color: text,
                                        border: `1px solid ${border}`,
                                        fontSize: 13,
                                        fontWeight: 500,
                                    }}
                                >
                                    <span>{category}</span>
                                    <span style={{ fontWeight: 700, opacity: 0.95 }}>{format(amount)}</span>
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DirectDebitRunSummary;
